// FastAI.js - Optimized AI for Connect4
import { getValidMoves, checkWin, Player, ROWS, COLS } from './gameLogic';

// Bot class implementation
class Connect4Bot {
  constructor() {
    this.moveCache = new Map(); // Cache for previously calculated moves
  }

  // Main function: Get the best move
  getBestMove(board, depth = 5) {
    const boardKey = this.getBoardKey(board);
    if (this.moveCache.has(boardKey)) {
      return this.moveCache.get(boardKey);
    }

    // Evaluate and sort moves first
    const moveScores = [];
    for (let col = 0; col < COLS; col++) {
      if (board[0][col] === Player.NONE) {
        moveScores.push({
          col: col,
          score: this.evaluateMove(board, col, Player.YELLOW)
        });
      }
    }

    // Sort moves by score (best first for better pruning)
    moveScores.sort((a, b) => b.score - a.score);

    let bestMove = -1;
    let maxEval = -Infinity;

    for (const moveScore of moveScores) {
      const col = moveScore.col;
      const row = this.makeMove(board, col, Player.YELLOW);
      if (row !== -1) {
        const eval_result = this.minimax(board, depth - 1, -Infinity, Infinity, false);
        this.undoMove(board, col);

        if (eval_result > maxEval) {
          maxEval = eval_result;
          bestMove = col;
        }
      }
    }

    // Cache the result
    if (bestMove !== -1) {
      this.moveCache.set(boardKey, bestMove);
    }

    return bestMove !== -1 ? bestMove : 3; // Default to center
  }

  // Minimax with alpha-beta pruning
  minimax(board, depth, alpha, beta, forPlayer) {
    // Terminal conditions
    if (checkWin(board, Player.YELLOW)) {
      return 2000 - depth; // Win for bot
    }
    if (checkWin(board, Player.RED)) {
      return -2000 + depth; // Win for opponent
    }
    if (depth === 0) {
      return 0; // Neutral evaluation at depth 0
    }

    // Get and sort moves for better pruning
    const moveScores = [];
    for (let col = 0; col < COLS; col++) {
      if (board[0][col] === Player.NONE) {
        const player = forPlayer ? Player.YELLOW : Player.RED;
        moveScores.push({
          col: col,
          score: this.evaluateMove(board, col, player)
        });
      }
    }
    moveScores.sort((a, b) => b.score - a.score);

    if (forPlayer) {
      // Maximizing player (bot)
      let maxEval = -Infinity;
      for (const moveScore of moveScores) {
        const col = moveScore.col;
        const row = this.makeMove(board, col, Player.YELLOW);
        if (row !== -1) {
          const eval_result = this.minimax(board, depth - 1, alpha, beta, false);
          this.undoMove(board, col);

          maxEval = Math.max(maxEval, eval_result);
          alpha = Math.max(alpha, eval_result);

          if (beta <= alpha) {
            break; // Beta cutoff
          }
        }
      }
      return maxEval;
    } else {
      // Minimizing player (human)
      let minEval = Infinity;
      for (const moveScore of moveScores) {
        const col = moveScore.col;
        const row = this.makeMove(board, col, Player.RED);
        if (row !== -1) {
          const eval_result = this.minimax(board, depth - 1, alpha, beta, true);
          this.undoMove(board, col);

          minEval = Math.min(minEval, eval_result);
          beta = Math.min(beta, eval_result);

          if (beta <= alpha) {
            break; // Alpha cutoff
          }
        }
      }
      return minEval;
    }
  }

  // Evaluate a move
  evaluateMove(board, col, currentPlayer) {
    let score = 0;

    // Check if this move results in a win
    if (this.canWin(board, col, currentPlayer)) {
      score += 1000;
    }

    // Check if this move blocks opponent's win
    const opponent = currentPlayer === Player.YELLOW ? Player.RED : Player.YELLOW;
    if (this.canWin(board, col, opponent)) {
      score += 500;
    }

    // Prefer center columns
    score += 50 * (3 - Math.abs(Math.floor(COLS / 2) - col));

    return score;
  }

  // Check if a move results in a win
  canWin(board, col, currentPlayer) {
    let row = -1;
    
    // Find the row where the piece would land
    for (row = ROWS - 1; row >= 0 && board[row][col] !== Player.NONE; row--) {}
    
    if (row < 0) {
      return false; // Column is full
    }

    // Temporarily place the piece
    board[row][col] = currentPlayer;
    const win = checkWin(board, currentPlayer);
    board[row][col] = Player.NONE; // Undo

    return win;
  }

  // Check if a move blocks opponent's win
  canBlock(board, col, currentPlayer) {
    const opponent = currentPlayer === Player.YELLOW ? Player.RED : Player.YELLOW;
    return this.canWin(board, col, opponent);
  }

  // Helper function to make a move and return the row
  makeMove(board, col, player) {
    if (col < 0 || col >= COLS || board[0][col] !== Player.NONE) {
      return -1;
    }

    for (let row = ROWS - 1; row >= 0; row--) {
      if (board[row][col] === Player.NONE) {
        board[row][col] = player;
        return row;
      }
    }
    return -1;
  }

  // Helper function to undo a move
  undoMove(board, col) {
    for (let row = 0; row < ROWS; row++) {
      if (board[row][col] !== Player.NONE) {
        board[row][col] = Player.NONE;
        return row;
      }
    }
    return -1;
  }

  // Generate a board key for caching
  getBoardKey(board) {
    return board.map(row => row.join('')).join('|');
  }

  // Clear cache (useful for new games)
  clearCache() {
    this.moveCache.clear();
  }
}

// Create a singleton bot instance
const botInstance = new Connect4Bot();

// Fast bot move using immediate win/block detection + lightweight evaluation
export const getLightningBotMove = (board) => {
  const validMoves = getValidMoves(board);
  if (validMoves.length === 0) return 3;

  // Priority 1: Check for immediate win
  for (const col of validMoves) {
    if (botInstance.canWin(board, col, Player.YELLOW)) {
      return col;
    }
  }

  // Priority 2: Check for immediate block
  for (const col of validMoves) {
    if (botInstance.canWin(board, col, Player.RED)) {
      return col;
    }
  }

  // Priority 3: Prefer center columns
  const centerPriority = [3, 2, 4, 1, 5, 0, 6];
  for (const col of centerPriority) {
    if (validMoves.includes(col)) {
      return col;
    }
  }

  return validMoves[0];
};

// Smart bot move using the full minimax algorithm
export const getSmartBotMove = (board, difficulty = 5) => {
  return botInstance.getBestMove(board, difficulty);
};

// Ultra-fast bot for instant response (< 5ms)
export const getUltraFastBotMove = (board) => {
  const validMoves = getValidMoves(board);
  if (validMoves.length === 0) return 3;

  // Only check for immediate win/block
  for (const col of validMoves) {
    if (botInstance.canWin(board, col, Player.YELLOW)) return col;
  }
  for (const col of validMoves) {
    if (botInstance.canWin(board, col, Player.RED)) return col;
  }

  // Return center or adjacent
  if (validMoves.includes(3)) return 3;
  if (validMoves.includes(2)) return 2;
  if (validMoves.includes(4)) return 4;
  return validMoves[0];
};

// Clear bot cache for new game
export const clearBotCache = () => {
  botInstance.clearCache();
};

export default botInstance;
